import React, { useState, useEffect, useCallback } from "react";
import FinancialCategoryService from "../../../../services/financial_category_service";
import { useTenant } from "../../../../hooks/useTenant";
import { FINANCIAL_TYPES } from "../../../../constants/financial";
import { NeoModal, NeoButton, NeoInput, NeoDatePicker } from "../../../../components/Common";

/**
 * PayableForm
 * Modal para criar uma nova despesa manual.
 */
const PayableForm = ({ isOpen, toggle, onSave, processing }) => {
    const { idTenant } = useTenant();
    const [categories, setCategories] = useState([]);
    const [loadingCategories, setLoadingCategories] = useState(true);

    const [formData, setFormData] = useState({
        description: "",
        amount: "",
        dueDate: new Date().toISOString().split("T")[0],
        competenceDate: new Date().toISOString().split("T")[0],
        recipient: "",
        idCategory: "",
        categoryName: "",
        observation: "",
    });

    // Carrega categorias do tipo EXPENSE
    useEffect(() => {
        if (!isOpen || !idTenant) return;
        setLoadingCategories(true);
        FinancialCategoryService.getAll(idTenant)
            .then(cats => {
                setCategories(cats.filter(c => c.type === FINANCIAL_TYPES.EXPENSE));
            })
            .catch(err => console.error("[PayableForm] Erro ao carregar categorias:", err))
            .finally(() => setLoadingCategories(false));
    }, [isOpen, idTenant]);

    const handleChange = useCallback((field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    }, []);

    const handleCategoryChange = useCallback((categoryId) => {
        const cat = categories.find(c => c.id === categoryId);
        setFormData(prev => ({
            ...prev,
            idCategory: categoryId,
            categoryName: cat?.name || "",
        }));
    }, [categories]);

    const handleSubmit = useCallback((e) => {
        if (e && e.preventDefault) e.preventDefault();
        if (!formData.description || !formData.amount || !formData.dueDate) return;
        onSave(formData);
    }, [formData, onSave]);

    const resetForm = useCallback(() => {
        setFormData({
            description: "",
            amount: "",
            dueDate: new Date().toISOString().split("T")[0],
            competenceDate: new Date().toISOString().split("T")[0],
            recipient: "",
            idCategory: "",
            categoryName: "",
            observation: "",
        });
    }, []);

    const isValid = formData.description && formData.amount && formData.dueDate;

    const modalFooterActions = (
        <>
            <NeoButton variant="light" className="text-muted px-4" onClick={toggle} disabled={processing}>
                Cancelar
            </NeoButton>
            <NeoButton
                variant="primary"
                onClick={handleSubmit}
                disabled={!isValid || processing}
                loading={processing}
                loadingText="Salvando..."
                icon="mdi-check"
                className="px-5 py-2 fw-bold"
            >
                Salvar Despesa
            </NeoButton>
        </>
    );

    return (
        <NeoModal
            isOpen={isOpen}
            toggle={toggle}
            size="lg"
            centered
            title="Nova Despesa"
            headerIcon="mdi-plus-circle-outline"
            footerActions={modalFooterActions}
            onClosed={resetForm}
        >
            <form onSubmit={handleSubmit}>
                <div className="row g-3">
                    <div className="col-md-8">
                        <label className="form-label small fw-bold text-muted text-uppercase mb-1" style={{ letterSpacing: "0.5px", fontFamily: "Outfit, sans-serif" }}>
                            Descrição *
                        </label>
                        <NeoInput
                            type="text"
                            value={formData.description}
                            onChange={(e) => handleChange("description", e.target.value)}
                            placeholder="Ex: Pagamento aluguel janeiro"
                            className="mb-0"
                            size="sm"
                        />
                    </div>
                    <div className="col-md-4">
                        <label className="form-label small fw-bold text-muted text-uppercase mb-1" style={{ letterSpacing: "0.5px", fontFamily: "Outfit, sans-serif" }}>
                            Valor *
                        </label>
                        <NeoInput
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.amount}
                            onChange={(e) => handleChange("amount", e.target.value)}
                            placeholder="0,00"
                            className="mb-0"
                            size="sm"
                        />
                    </div>
                    <div className="col-md-4">
                        <label className="form-label small fw-bold text-muted text-uppercase mb-1" style={{ letterSpacing: "0.5px", fontFamily: "Outfit, sans-serif" }}>
                            Vencimento *
                        </label>
                        <NeoDatePicker
                            value={formData.dueDate}
                            onChange={(dateStr) => handleChange("dueDate", dateStr)}
                            size="sm"
                            className="mb-0 w-100"
                        />
                    </div>
                    <div className="col-md-4">
                        <label className="form-label small fw-bold text-muted text-uppercase mb-1" style={{ letterSpacing: "0.5px", fontFamily: "Outfit, sans-serif" }}>
                            Competência
                        </label>
                        <NeoDatePicker
                            value={formData.competenceDate}
                            onChange={(dateStr) => handleChange("competenceDate", dateStr)}
                            size="sm"
                            className="mb-0 w-100"
                        />
                    </div>
                    <div className="col-md-4">
                        <label className="form-label small fw-bold text-muted text-uppercase mb-1" style={{ letterSpacing: "0.5px", fontFamily: "Outfit, sans-serif" }}>
                            Categoria
                        </label>
                        {loadingCategories ? (
                            <div className="d-flex align-items-center py-1 gap-2">
                                <div className="spinner-border spinner-border-sm text-primary" role="status" style={{ width: "14px", height: "14px" }} />
                                <small className="text-muted" style={{ fontSize: "0.75rem", fontFamily: "Outfit, sans-serif" }}>Carregando...</small>
                            </div>
                        ) : (
                            <NeoInput
                                type="select"
                                value={formData.idCategory}
                                onChange={(e) => handleCategoryChange(e.target.value)}
                                className="mb-0"
                                size="sm"
                            >
                                <option value="">Selecione...</option>
                                {categories.map(c => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </NeoInput>
                        )}
                    </div>
                    <div className="col-md-6">
                        <label className="form-label small fw-bold text-muted text-uppercase mb-1" style={{ letterSpacing: "0.5px", fontFamily: "Outfit, sans-serif" }}>
                            Fornecedor / Beneficiário
                        </label>
                        <NeoInput
                            type="text"
                            value={formData.recipient}
                            onChange={(e) => handleChange("recipient", e.target.value)}
                            placeholder="Nome do fornecedor"
                            className="mb-0"
                            size="sm"
                        />
                    </div>
                    <div className="col-md-6">
                        <label className="form-label small fw-bold text-muted text-uppercase mb-1" style={{ letterSpacing: "0.5px", fontFamily: "Outfit, sans-serif" }}>
                            Observação
                        </label>
                        <NeoInput
                            type="text"
                            value={formData.observation}
                            onChange={(e) => handleChange("observation", e.target.value)}
                            placeholder="Nota interna (opcional)"
                            className="mb-0"
                            size="sm"
                        />
                    </div>
                </div>
            </form>
        </NeoModal>
    );
};

export default PayableForm;
